/**
 * 个股 WebSocket 实时行情展示指令。
 *
 * 在元素进入视口时按 `fields` 订阅 stock 模块行情，离开时退订；展示文案支持
 * `formatter` 函数优先，其次 `preset`（可由 value.preset 或修饰符 `.price` / `.pct` / `.full` 推断），否则默认「原文本 + 价 + 涨跌幅」。
 *
 * 用法：
 *   - `v-stock-realtime="{ fullCode, fields, formatter?, preset?, changePct?, colorClass? }"` 显式传 fields
 *   - `v-stock-realtime.price="..."` / `.pct="..."` / `.full="..."` 由修饰符推断 fields
 *   - 兼容直接传 stock 行项：`v-stock-realtime="stockItem"`，由修饰符 / 默认值推断 fields
 *   - 默认按 `changePct`（快照优先，其次 value.changePct）设置涨跌色 class（`getStockClassWithDefaultGray`）
 *   - `.no-color` 或 `colorClass: false` 可关闭自动上色
 *
 * 修饰符到 fields 推断：
 *   - `.price`           → `['lastPrice']`
 *   - `.pct` / `.change` → `['changePct']`
 *   - `.full` / 无修饰符 → `['lastPrice', 'changePct']`
 *
 * @module common/directive/stock-realtime
 */

import {
  clone,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isPlainObject,
  isString,
  trim
} from 'lodash-es';
import store from '@/store';
import { clearStockColorClass, formatStockChangePct, formatStockPrice, syncStockColorClass } from '@/utils/biz/stock';
import { STOCK_REALTIME_DEFAULT_FIELDS } from '@/utils/realtime/stockChannels';

/** 已注册到元素的色 class 缓存键，避免与 v-format 等其它指令互相覆盖 */
const COLOR_CLASS_KEY = '_vtColorClass';

/**
 * 从 binding 解析股票代码。
 * @param {{ value: unknown, modifiers?: Record<string, boolean> }} binding Vue 指令 binding
 * @returns {string}
 */
function resolveStockCode(binding) {
  const val = binding.value;
  if (!val) return '';
  if (isPlainObject(val)) {
    if (val.fullCode) {
      return trim(String(val.fullCode));
    }
    // 兼容历史 stock 数据形态：market + code 拼接
    if (val.code && val.market) {
      return `${String(val.market).toUpperCase()}.${trim(String(val.code))}`;
    }
  }
  if (isString(val)) {
    return trim(val);
  }
  return '';
}

/**
 * 解析展示相关选项（formatter / preset）。
 * `preset` 优先取 binding.value.preset；未传时由修饰符推断（与 fields 推断规则对应）。
 * @param {{ value: unknown, modifiers?: Record<string, boolean> }} binding Vue 指令 binding
 * @returns {{ formatter: Function | null, preset: string | null }}
 */
function extractFormatOptions(binding) {
  const val = binding.value;
  /** @type {{ formatter: Function | null, preset: string | null }} */
  const out = { formatter: null, preset: null };
  if (isPlainObject(val)) {
    if (isFunction(val.formatter)) {
      out.formatter = val.formatter;
    }
    if (val.preset) {
      out.preset = String(val.preset);
    }
  }
  if (!out.preset) {
    const m = binding.modifiers || {};
    if (m.price) {
      // 修饰符 .price：仅展示价格
      out.preset = 'price';
    } else if (m.pct || m.change) {
      // 修饰符 .pct / .change：仅展示涨跌幅
      out.preset = 'pct';
    } else if (m.full) {
      // 修饰符 .full：原文 + 价 + 涨跌幅
      out.preset = 'full';
    }
  }
  return out;
}

/**
 * 是否根据涨跌幅自动设置颜色 class。
 * @param {{ value: unknown, modifiers?: Record<string, boolean> }} binding Vue 指令 binding
 * @returns {boolean}
 */
function shouldApplyColorClass(binding) {
  const val = binding.value;
  if (isPlainObject(val) && val.colorClass === false) {
    return false;
  }
  if (binding.modifiers?.noColor) {
    return false;
  }
  return true;
}

/**
 * 从 binding 读取列表行上的涨跌幅兜底（快照未到时用于上色）。
 * @param {{ value: unknown }} binding Vue 指令 binding
 * @returns {number|string|null|undefined}
 */
function resolveFallbackChangePct(binding) {
  const val = binding.value;
  if (isPlainObject(val) && !isNil(val.changePct)) {
    return val.changePct;
  }
  return null;
}

/**
 * 推断本次订阅需要的 fields：value 显式 fields > 修饰符 > 默认值。
 * 启用涨跌色时确保订阅 `changePct`（`.price` 等仅价字段场景亦需涨跌幅上色）。
 * @param {{ value: unknown, modifiers?: Record<string, boolean> }} binding Vue 指令 binding
 * @returns {string[]}
 */
function resolveFields(binding) {
  const val = binding.value;
  let fields;
  if (isPlainObject(val) && isArray(val.fields) && !isEmpty(val.fields)) {
    // 显式 fields 优先级最高；clone 避免外部修改影响订阅态
    fields = clone(val.fields);
  } else {
    const m = binding.modifiers || {};
    if (m.price) {
      fields = ['lastPrice'];
    } else if (m.pct || m.change) {
      fields = ['changePct'];
    } else {
      // .full 与无修饰符都使用默认完整字段集，保持与历史行为一致
      fields = clone(STOCK_REALTIME_DEFAULT_FIELDS);
    }
  }
  if (shouldApplyColorClass(binding) && !includes(fields, 'changePct')) {
    fields = [...fields, 'changePct'];
  }
  return fields;
}

/**
 * 按涨跌幅更新元素涨跌色 class（国内涨红跌绿，海外相反；无效值灰色）。
 *
 * 实时行情场景下需要"无值也保留兜底灰"——避免快照未到达时短暂掉色；
 * 因此调用工具时打开 `fallbackToGray`，与 v-format 的"无值不染色"策略区分。
 *
 * @param {HTMLElement} el
 * @param {{ fullCode: string, quote: object | null }} snapshot
 * @param {number|string|null|undefined} fallbackChangePct 快照未到时的兜底涨跌幅
 * @returns {void}
 */
function applyStockColorClass(el, snapshot, fallbackChangePct) {
  const fromQuote = snapshot?.quote?.changePct;
  const value = !isNil(fromQuote) ? fromQuote : fallbackChangePct;
  syncStockColorClass(el, value, COLOR_CLASS_KEY, { fallbackToGray: true });
}

/**
 * 组装当前快照，供 formatter 或预设渲染使用。
 * @param {string} fullCode
 * @returns {{ fullCode: string, quote: object | null }}
 */
function buildSnapshot(fullCode) {
  const node = store.state.stock.snapshotsByFullCode[fullCode] ?? null;
  const quote = node?.quote ?? null;
  return { fullCode, quote };
}

/**
 * 默认展示：原文本 + 价 + 涨跌幅（有则拼接）。
 * @param {{ fullCode: string, quote: object | null }} snapshot
 * @param {string} originalText
 * @returns {string}
 */
function formatDefault(snapshot, originalText) {
  const q = snapshot.quote;
  if (!q) {
    // 行情快照尚未到达：保留原文，避免渲染出 '-- --' 等占位
    return originalText;
  }
  // 兼容老 price 字段；新数据走 lastPrice
  const priceVal = q.lastPrice ?? q.price;
  const hasPrice = !isNil(priceVal);
  const hasPct = !isNil(q.changePct);
  if (!hasPrice && !hasPct) {
    // 行情字段都缺失：同样保留原文，避免覆盖业务原始展示
    return originalText;
  }
  const parts = [originalText];
  if (hasPrice) {
    // 有效价格统一走标准格式化（两位小数）
    parts.push(formatStockPrice(priceVal));
  }
  if (hasPct) {
    // 有效涨跌幅统一走标准格式化（+1.23% / -1.23% / 0% / --）
    parts.push(formatStockChangePct(q.changePct));
  }
  return parts.join(' ');
}

/**
 * 按内置 preset 渲染。
 * @param {{ fullCode: string, quote: object | null }} snapshot
 * @param {string} preset
 * @param {string} originalText
 * @returns {string}
 */
function formatByPreset(snapshot, preset, originalText) {
  const q = snapshot.quote || {};
  const priceVal = q.lastPrice ?? q.price;
  if (preset === 'price') {
    // 仅展示价格：缺省时退回原文，避免把业务原始文本覆盖成 '--'
    if (isNil(priceVal)) {
      return originalText;
    }
    return formatStockPrice(priceVal);
  }
  if (includes(['pct', 'change'], preset)) {
    // 仅展示涨跌幅：缺省时同样退回原文
    if (isNil(q.changePct)) {
      return originalText;
    }
    return formatStockChangePct(q.changePct);
  }
  if (preset === 'full') {
    // full 与默认行为一致：原文 + 价 + 涨跌幅
    return formatDefault(snapshot, originalText);
  }
  // 未识别 preset：兜底走默认渲染
  return formatDefault(snapshot, originalText);
}

/**
 * 根据选项渲染文本并写入元素。
 * @param {HTMLElement} el
 * @param {string} stockCode
 * @param {{ formatter: Function | null, preset: string | null }} formatOpts
 * @param {number|string|null|undefined} fallbackChangePct
 * @param {boolean} applyColorClass
 */
function applyRender(el, stockCode, formatOpts, fallbackChangePct, applyColorClass) {
  const snap = buildSnapshot(stockCode);
  let text;
  if (formatOpts.formatter) {
    try {
      text = formatOpts.formatter(snap);
    } catch (err) {
      console.error('[v-stock-realtime] formatter error', err);
      text = el._vtOriginalText;
    }
  } else if (formatOpts.preset) {
    text = formatByPreset(snap, formatOpts.preset, el._vtOriginalText);
  } else {
    text = formatDefault(snap, el._vtOriginalText);
  }
  el.textContent = isNil(text) ? el._vtOriginalText : String(text);
  if (applyColorClass) {
    applyStockColorClass(el, snap, fallbackChangePct);
  }
}

/**
 * 停止订阅并移除 store 监听。
 * @param {HTMLElement} el
 * @param {string} stockCode
 * @param {string[]} fields 订阅时使用的 fields，必须与 startWatching 时保持一致
 */
function stopWatching(el, stockCode, fields) {
  if (stockCode && isArray(fields) && !isEmpty(fields)) {
    store.dispatch('stock/unsubscribeCodes', { fullCodes: [stockCode], fields });
  }
  if (el._vtUnwatch) {
    el._vtUnwatch();
    el._vtUnwatch = null;
  }
  el.textContent = el._vtOriginalText;
  if (el._vtApplyColorClass) {
    clearStockColorClass(el, COLOR_CLASS_KEY);
  }
}

/**
 * 开始订阅并监听快照变化以刷新 DOM。
 * @param {HTMLElement} el
 * @param {string} stockCode
 * @param {{ formatter: Function | null, preset: string | null }} formatOpts
 * @param {string[]} fields 本次订阅的字段集合
 */
function startWatching(el, stockCode, formatOpts, fields) {
  store.dispatch('stock/subscribeCodes', { fullCodes: [stockCode], fields });
  el._vtUnwatch = store.watch(
    () => store.state.stock.snapshotsByFullCode[stockCode],
    () => {
      applyRender(el, stockCode, formatOpts, el._vtFallbackChangePct, el._vtApplyColorClass);
    },
    { deep: true, immediate: true }
  );
}

const stockRealtimeDirective = {
  mounted(el, binding) {
    if (!binding.value) {
      return;
    }

    const stockCode = resolveStockCode(binding);
    if (!stockCode) {
      return;
    }

    const fields = resolveFields(binding);

    el._vtStockCode = stockCode;
    el._vtFields = fields;
    el._vtOriginalText = el.textContent;
    const formatOpts = extractFormatOptions(binding);
    el._vtFormatOpts = formatOpts;
    el._vtFallbackChangePct = resolveFallbackChangePct(binding);
    el._vtApplyColorClass = shouldApplyColorClass(binding);
    if (el._vtApplyColorClass) {
      applyStockColorClass(el, buildSnapshot(stockCode), el._vtFallbackChangePct);
    }

    // 视口外不启动订阅，节省 WS 流量；进入视口才订阅，离开立即退订
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startWatching(el, stockCode, formatOpts, fields);
        } else {
          stopWatching(el, stockCode, fields);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    el._vtObserver = observer;
  },

  updated(el, binding) {
    const nextCode = resolveStockCode(binding);
    const nextFields = resolveFields(binding);
    const codeChanged = nextCode !== el._vtStockCode;
    const fieldsChanged = !isEqual(nextFields, el._vtFields);
    if (codeChanged || fieldsChanged) {
      // stock code 或 fields 变化：把旧订阅退掉，重建 observer 与订阅
      el._vtObserverGeneration = (el._vtObserverGeneration || 0) + 1;
      if (el._vtObserver) {
        try {
          el._vtObserver.disconnect();
        } catch (e) {
          console.error(e);
        }
        el._vtObserver = null;
      }
      stopWatching(el, el._vtStockCode, el._vtFields);
      el._vtStockCode = nextCode;
      el._vtFields = nextFields;
      el._vtFormatOpts = extractFormatOptions(binding);
      el._vtFallbackChangePct = resolveFallbackChangePct(binding);
      el._vtApplyColorClass = shouldApplyColorClass(binding);
      if (!nextCode || !binding.value) {
        return;
      }
      const observerGeneration = el._vtObserverGeneration;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (el._vtObserverGeneration !== observerGeneration) {
            return;
          }
          if (entry.isIntersecting) {
            startWatching(el, nextCode, el._vtFormatOpts, nextFields);
          } else {
            stopWatching(el, nextCode, nextFields);
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(el);
      el._vtObserver = observer;
      return;
    }
    el._vtFormatOpts = extractFormatOptions(binding);
    el._vtFallbackChangePct = resolveFallbackChangePct(binding);
    el._vtApplyColorClass = shouldApplyColorClass(binding);
    if (el._vtUnwatch && el._vtStockCode) {
      applyRender(el, el._vtStockCode, el._vtFormatOpts, el._vtFallbackChangePct, el._vtApplyColorClass);
    } else if (el._vtApplyColorClass && el._vtStockCode) {
      applyStockColorClass(el, buildSnapshot(el._vtStockCode), el._vtFallbackChangePct);
    } else if (!el._vtApplyColorClass) {
      clearStockColorClass(el, COLOR_CLASS_KEY);
    }
  },

  unmounted(el) {
    if (el._vtObserver) {
      try {
        el._vtObserver.unobserve(el);
        el._vtObserver.disconnect();
      } catch (e) {
        console.error(e);
      }
      el._vtObserver = null;
    }
    if (el._vtStockCode) {
      stopWatching(el, el._vtStockCode, el._vtFields);
      el._vtStockCode = null;
      el._vtFields = null;
    }
  }
};

export default stockRealtimeDirective;
